import { roundMoney } from "./formatters";
import { isInWindow, isBeforeWindow } from "./cierrePeriod";
import { esUnidadDelEdificio } from "./unidadesEdificio";
import { nombrePeriodo } from "./alquileresEdificio";

/**
 * Cálculos de balances multi-fondo para propiedad horizontal.
 *
 * Fondos built-in:
 * - Caja (Gastos Comunes): operación mensual ordinaria
 * - Reserva: cuota mensual (% asamblea) + multas + intereses mora + gastos de obras por defecto
 *
 * Fondos personalizados (FIN-019): el admin puede crear fondos adicionales (Calefacción,
 * Obra Fachada, etc.) que se devuelven bajo su propio id además de caja/reserva.
 *
 * Nota: los gastos con categoría "obra" se debitan del fondo Reserva por defecto.
 *       Si el edificio quiere un bucket separado para Obras, debe crearlo como fondo custom.
 *
 * Cada fondo: { saldoAnterior, cobranza, gastos, creditos, debitos, saldoActual }
 *
 * Alquiler del edificio (30-oct-2026): lo que paga el inquilino de una unidad del
 * edificio (línea `alquiler` del pago) NO es cobranza: es un ingreso del edificio al
 * fondo del contrato (rentFundId). Los motores lo suman como un ingreso más
 * (ingresosDeAlquiler), igual que los de Ingresos del edificio.
 */

/**
 * Calcula los balances de los fondos para un período dado.
 *
 * @param {Object} params
 * @param {Array} params.expenses - Gastos del edificio [{amount, expenseCategory, money, invoiceDate}]
 * @param {Array} params.payments - Pagos del edificio [{amount, paid, paidAmount, breakdown, month, year, status}]
 * @param {Array} params.multas - Multas [{amount, status, feedsReserveFund}]
 * @param {Object} params.building - Config edificio {initialBankBalance, initialReserveFund, customFunds, ...USD}
 * @param {number} params.year - Año del período
 * @param {number} params.month - Mes del período (1-12)
 * @param {string} [params.currency="peso"] - "peso" | "dolar"
 * @returns {{ caja: FundBalance, reserva: FundBalance, [customFundId]: FundBalance, total: FundBalance }}
 */
export function calculateFundBalances({ expenses, payments, multas, incomes, building, year, month, currency = "peso", periodStart = null, periodEnd = null }) {
  const isUSD = currency === "dolar";

  // Ventana de corte configurable: cuando el cierre define [periodStart, periodEnd],
  // los gastos y los ingresos se bucketean por esa ventana ((start, end], por su
  // fecha de PAGO/cobro), no por el mes calendario del invoiceDate. Sin ventana →
  // mes calendario. Los cobros de residentes van por su FECHA DE COBRO (igual que
  // el Estado de cuenta / fundMovements), NO por la etiqueta del pago: antes un
  // pago "Agosto" cobrado en octubre caía en el saldo anterior del PDF de
  // septiembre mientras los gastos de octubre sí entraban → saldo distinto al
  // del Estado de cuenta.
  const useWindow = periodStart != null && periodEnd != null;
  const expDate = (e) => parseDate(e.paidDate) || parseDate(e.invoiceDate);
  const expInPeriod = (e) => {
    const d = expDate(e);
    if (!d) return false;
    return useWindow
      ? isInWindow(d, periodStart, periodEnd)
      : d.getFullYear() === year && d.getMonth() + 1 === month;
  };
  const expBeforePeriod = (e) => {
    const d = expDate(e);
    if (!d) return false;
    return useWindow
      ? isBeforeWindow(d, periodStart)
      : d.getFullYear() < year || (d.getFullYear() === year && d.getMonth() + 1 < month);
  };
  const dateInPeriod = (d) => {
    if (!d) return false;
    return useWindow
      ? isInWindow(d, periodStart, periodEnd)
      : d.getFullYear() === year && d.getMonth() + 1 === month;
  };
  const dateBeforePeriod = (d) => {
    if (!d) return false;
    return useWindow
      ? isBeforeWindow(d, periodStart)
      : d.getFullYear() < year || (d.getFullYear() === year && d.getMonth() + 1 < month);
  };

  // Fecha de cobro de un pago de residente (ver fechaDeCobro). Si no tiene
  // ninguna, cae a la etiqueta (month, year).
  const payDate = fechaDeCobro;
  const payInPeriod = (p) => {
    const d = payDate(p);
    return d ? dateInPeriod(d) : (p.month === month && p.year === year);
  };
  const payBeforePeriod = (p) => {
    const d = payDate(p);
    return d ? dateBeforePeriod(d) : (p.year < year || (p.year === year && p.month < month));
  };

  // Initial balances from building config
  const initialCaja = Number(isUSD ? building?.initialBankBalanceUSD : building?.initialBankBalance) || 0;
  const initialReserva = Number(isUSD ? building?.initialReserveFundUSD : building?.initialReserveFund) || 0;

  // Filter by currency
  const currencyFilter = (exp) => {
    const m = (exp.money || "peso").toLowerCase();
    return isUSD ? m === "dolar" : m !== "dolar";
  };

  // Cobranza por moneda: payments y multas se guardan hoy en pesos (los cargos
  // en USD se convierten a UYU en el cierre). Filtramos por su campo de moneda
  // para que el resumen en dólares solo sume la cobranza en dólares. Sin campo
  // de moneda → se considera peso (comportamiento histórico).
  const incomeCurrencyFilter = (doc) => {
    const raw = String(doc.money || doc.currency || "peso").toLowerCase();
    const isDolar = raw === "dolar" || raw === "usd" || raw === "us$";
    return isUSD ? isDolar : !isDolar;
  };

  // Split expenses by category for this month (o ventana de corte)
  // Solo gastos PAGADOS (flujo de caja, igual que el Estado de cuenta): un gasto
  // impago no sacó plata de la cuenta. Antes se contaba por su fecha de factura
  // y el saldo de caja del PDF difería del Estado de cuenta.
  const monthExpenses = expenses.filter((e) => e.paid && currencyFilter(e) && expInPeriod(e));

  // Mapa de gastos por fondo custom: { [fundId]: total }
  // Se rellena cuando un gasto de categoría "obra" o "incidencia" tiene e.sourceFund = <customFundId>.
  const gastosCustomByFund = {};
  let gastosCaja = 0, gastosReserva = 0;
  monthExpenses.forEach((e) => {
    const amt = Number(e.amount) || 0;
    const cat = e.expenseCategory || "gastos_comunes";
    const src = e.sourceFund; // "caja" | "reserva" | customFundId | null
    if (src === "caja") {
      gastosCaja += amt;
    } else if (src === "reserva") {
      gastosReserva += amt;
    } else if (src && typeof src === "string") {
      // Fondo personalizado elegido por el admin al cargar el gasto
      gastosCustomByFund[src] = (gastosCustomByFund[src] || 0) + amt;
    } else if (cat === "obra") {
      // Default legacy: obra → reserva
      gastosReserva += amt;
    } else {
      // gastos_comunes, incidencia sin sourceFund, legacy → caja
      gastosCaja += amt;
    }
  });

  // Payments for this month (approved/paid)
  const monthPayments = payments.filter((p) =>
    (p.status === "aprobado" || p.paid) && incomeCurrencyFilter(p) && payInPeriod(p)
  );

  let cobranzaCaja = 0, cobranzaReserva = 0;
  const customFundCobranza = {};
  monthPayments.forEach((p) => {
    // Excedente del sobrepago = saldo a favor (pool aparte), NO entra a los fondos.
    const amt = Number(p.amount) || 0;
    const paidRaw = Number(p.paidAmount) || amt || 0;
    const paid = amt > 0 ? Math.min(paidRaw, amt) : paidRaw;
    if (p.breakdown) {
      const result = distributeBreakdown(p.breakdown, p, paid);
      cobranzaCaja += result.caja;
      cobranzaReserva += result.reserva;
      Object.entries(result.custom).forEach(([fid, amt]) => {
        customFundCobranza[fid] = (customFundCobranza[fid] || 0) + amt;
      });
      if (result.hasData) return;
    }
    cobranzaCaja += paid;
  });

  // Multas pagadas: distribuir por fondo destino
  (multas || [])
    .filter((m) => m.status === "pagada" && incomeCurrencyFilter(m))
    .forEach((m) => {
      const amt = Number(m.amount) || 0;
      const fundId = m.targetFundId || (m.feedsReserveFund ? "reserva" : "caja");
      if (fundId === "reserva") {
        cobranzaReserva += amt;
      } else if (fundId === "caja") {
        cobranzaCaja += amt;
      } else {
        customFundCobranza[fundId] = (customFundCobranza[fundId] || 0) + amt;
      }
    });

  // Ingresos del edificio (buildingincomes) cobrados este mes, ruteados a su fondo
  // destino (targetFundId, default caja). Devengado por la fecha de cobro.
  // Desde 24-sep-2026 se reportan como CRÉDITOS (columna aparte del resumen por
  // fondos), no dentro de la cobranza de residentes. El saldoActual no cambia:
  // saldoActual = anterior + cobranza + créditos − gastos − débitos.
  let creditosCaja = 0, creditosReserva = 0;
  const customFundCreditos = {};
  // + alquileres cobrados de unidades del edificio (ingresos, no cobranza).
  const ingresos = [...(incomes || []), ...ingresosDeAlquiler(payments)];
  ingresos
    .filter((inc) => inc.status === "cobrado" && incomeCurrencyFilter(inc))
    .forEach((inc) => {
      const d = parseDate(inc.receivedAt) || parseDate(inc.date) || parseDate(inc.createdAt);
      if (!dateInPeriod(d)) return;
      const amt = Number(inc.amount) || 0;
      const fund = inc.targetFundId || "caja";
      if (fund === "reserva") creditosReserva += amt;
      else if (fund === "caja") creditosCaja += amt;
      else customFundCreditos[fund] = (customFundCreditos[fund] || 0) + amt;
    });

  // Calculate saldo anterior: initial + sum of all previous months
  const prevPayments = payments.filter((p) =>
    (p.status === "aprobado" || p.paid) && incomeCurrencyFilter(p) && payBeforePeriod(p)
  );
  const prevExpenses = expenses.filter((e) => e.paid && currencyFilter(e) && expBeforePeriod(e));

  let prevCobrCaja = 0, prevCobrReserva = 0;
  const prevCustomFundCobranza = {};
  prevPayments.forEach((p) => {
    // Excedente del sobrepago = saldo a favor (pool aparte), NO entra a los fondos.
    const amt = Number(p.amount) || 0;
    const paidRaw = Number(p.paidAmount) || amt || 0;
    const paid = amt > 0 ? Math.min(paidRaw, amt) : paidRaw;
    if (p.breakdown) {
      const result = distributeBreakdown(p.breakdown, p, paid);
      prevCobrCaja += result.caja;
      prevCobrReserva += result.reserva;
      Object.entries(result.custom).forEach(([fid, amt]) => {
        prevCustomFundCobranza[fid] = (prevCustomFundCobranza[fid] || 0) + amt;
      });
      if (result.hasData) return;
    }
    prevCobrCaja += paid;
  });

  // Ingresos de meses PREVIOS → saldo anterior del fondo destino.
  ingresos
    .filter((inc) => inc.status === "cobrado" && incomeCurrencyFilter(inc))
    .forEach((inc) => {
      const d = parseDate(inc.receivedAt) || parseDate(inc.date) || parseDate(inc.createdAt);
      if (!dateBeforePeriod(d)) return;
      const amt = Number(inc.amount) || 0;
      const fund = inc.targetFundId || "caja";
      if (fund === "reserva") prevCobrReserva += amt;
      else if (fund === "caja") prevCobrCaja += amt;
      else prevCustomFundCobranza[fund] = (prevCustomFundCobranza[fund] || 0) + amt;
    });

  // Gastos de meses previos — misma lógica que el mes actual, respetando e.sourceFund.
  const prevGastCustomByFund = {};
  let prevGastCaja = 0, prevGastReserva = 0;
  prevExpenses.forEach((e) => {
    const amt = Number(e.amount) || 0;
    const cat = e.expenseCategory || "gastos_comunes";
    const src = e.sourceFund;
    if (src === "caja") {
      prevGastCaja += amt;
    } else if (src === "reserva") {
      prevGastReserva += amt;
    } else if (src && typeof src === "string") {
      prevGastCustomByFund[src] = (prevGastCustomByFund[src] || 0) + amt;
    } else if (cat === "obra") {
      prevGastReserva += amt;
    } else {
      prevGastCaja += amt;
    }
  });

  const saldoAntCaja = initialCaja + prevCobrCaja - prevGastCaja;
  const saldoAntReserva = initialReserva + prevCobrReserva - prevGastReserva;

  // creditos = ingresos del edificio del período; debitos queda reservado para
  // futuros egresos que no sean gastos (hoy no existe nada que lo alimente).
  const buildFund = (saldoAnterior, cobranza, gastos, creditos = 0, debitos = 0) => {
    const saldoActual = roundMoney(saldoAnterior + cobranza + creditos - gastos - debitos);
    return {
      saldoAnterior: roundMoney(saldoAnterior),
      cobranza: roundMoney(cobranza),
      gastos: roundMoney(gastos),
      creditos: roundMoney(creditos),
      debitos: roundMoney(debitos),
      saldoActual,
    };
  };

  const caja = buildFund(saldoAntCaja, cobranzaCaja, gastosCaja, creditosCaja);
  const reserva = buildFund(saldoAntReserva, cobranzaReserva, gastosReserva, creditosReserva);

  // Fondos personalizados (FIN-019)
  const customFundResults = {};
  (building?.customFunds || []).filter(f => f.active !== false).forEach(fund => {
    const initial = Number(isUSD ? fund.initialBalanceUSD : fund.initialBalance) || 0;

    // Cobranza del mes actual y de meses anteriores. Los breakdown keys
    // `fondo_<id>` ya fueron ruteados por distributeBreakdown (con ratio de
    // pago parcial) a customFundCobranza / prevCustomFundCobranza — volver a
    // sumarlos aca duplicaba la cobranza de fondos custom.
    const cobranza = customFundCobranza[fund.id] || 0;
    const prevCobr = prevCustomFundCobranza[fund.id] || 0;

    // Gastos de este mes y de meses previos asignados a este fondo por e.sourceFund.
    const gastosFund = gastosCustomByFund[fund.id] || 0;
    const prevGastFund = prevGastCustomByFund[fund.id] || 0;

    const saldoAnt = initial + prevCobr - prevGastFund;
    customFundResults[fund.id] = buildFund(saldoAnt, cobranza, gastosFund, customFundCreditos[fund.id] || 0);
  });

  const total = {
    saldoAnterior: caja.saldoAnterior + reserva.saldoAnterior +
      Object.values(customFundResults).reduce((s, f) => s + f.saldoAnterior, 0),
    cobranza: caja.cobranza + reserva.cobranza +
      Object.values(customFundResults).reduce((s, f) => s + f.cobranza, 0),
    gastos: caja.gastos + reserva.gastos +
      Object.values(customFundResults).reduce((s, f) => s + f.gastos, 0),
    creditos: caja.creditos + reserva.creditos +
      Object.values(customFundResults).reduce((s, f) => s + f.creditos, 0),
    debitos: caja.debitos + reserva.debitos +
      Object.values(customFundResults).reduce((s, f) => s + f.debitos, 0),
    saldoActual: caja.saldoActual + reserva.saldoActual +
      Object.values(customFundResults).reduce((s, f) => s + f.saldoActual, 0),
  };

  return { caja, reserva, ...customFundResults, total };
}

/**
 * Fecha de cobro de un pago de residente: paymentDate → adminPaymentDate →
 * verifiedAt → createdAt. Mismo criterio en el resumen por fondo, en la tabla
 * por unidad y en el Estado de cuenta (fundMovements).
 * @returns {Date|null}
 */
export function fechaDeCobro(p) {
  return parseDate(p?.paymentDate) || parseDate(p?.adminPaymentDate) || parseDate(p?.verifiedAt) || parseDate(p?.createdAt);
}

/**
 * Alquileres cobrados de unidades del edificio, como ingresos (mismo formato que
 * buildingincomes): uno por pago aprobado/pagado con línea `alquiler`, al fondo del
 * contrato (rentFundId), con la fecha de cobro del pago. Si el pago es parcial
 * entra la misma proporción que el resto de las líneas (distributeBreakdown). Sin
 * el nombre del inquilino: el concepto dice unidad y período.
 * @param {Array} payments - pagos del edificio
 * @returns {Array<{id, paymentId, apartmentId, concept, amount, targetFundId, receivedAt, status, type, source, currency, rentPeriod, ref}>}
 */
export function ingresosDeAlquiler(payments) {
  const out = [];
  for (const p of payments || []) {
    if (!p || !(p.status === "aprobado" || p.paid)) continue;
    if (!p.breakdown || !(Number(p.breakdown.alquiler) > 0)) continue;
    const amt = Number(p.amount) || 0;
    const paidRaw = Number(p.paidAmount) || amt || 0;
    const paid = amt > 0 ? Math.min(paidRaw, amt) : paidRaw;
    const amount = roundMoney(distributeBreakdown(p.breakdown, p, paid).alquiler);
    if (!(amount > 0)) continue;
    const periodo = p.rentPeriod || (p.month && p.year ? { month: p.month, year: p.year } : null);
    const unidad = p.apartmentNumber ? `unidad ${p.apartmentNumber}` : "unidad del edificio";
    out.push({
      id: `alq_${p.id}`,
      paymentId: p.id,
      apartmentId: p.apartmentId || null,
      concept: `Alquiler ${unidad}${periodo ? ` — ${nombrePeriodo(periodo)}` : ""}`,
      amount,
      targetFundId: p.rentFundId || "reserva",
      receivedAt: fechaDeCobro(p),
      status: "cobrado",
      type: "alquiler",
      source: "cobranza",
      currency: "UYU",
      rentPeriod: periodo,
      ref: `ALQ-${p.apartmentNumber || String(p.id || "").slice(-4)}`,
    });
  }
  return out;
}

/**
 * Plata que entró por un pago: lo verificado (aprobado o parcial), sin lo que se
 * cubrió con saldo a favor (paidFromCredit, ya contado en el sobrepago que lo
 * generó). Lo que el residente informó y nadie revisó todavía no cuenta: de un
 * pendiente_verificacion solo vale lo verificado antes (paidAmount − lo
 * informado). Un rechazado ya tiene el paidAmount revertido.
 */
export function cobradoDelPago(p) {
  if (!p) return 0;
  const pagado = Number(p.paidAmount) || (p.paid ? Number(p.amount) || 0 : 0);
  const sinVerificar = p.status === "pendiente_verificacion" ? Number(p.lastPaymentAttempt) || 0 : 0;
  return Math.max(0, pagado - sinVerificar - (Number(p.paidFromCredit) || 0));
}

/**
 * Tabla por unidad del Informe (formato Park Ltda: 001P, 001I) como cuenta
 * corriente de cada responsable:
 *
 * - Cargos del mes: las cuotas con etiqueta (month, year) del mes (las del
 *   cierre y las de saldo inicial de la migración).
 * - Pagos: lo cobrado dentro del período por su FECHA DE COBRO, sea de la cuota
 *   que sea (lo pagado en octubre va en octubre aunque sea la cuota de
 *   septiembre).
 * - Saldo anterior = Total a pagar del mes anterior = Σ cargos de meses
 *   anteriores − Σ cobrado antes del período. Un sobrepago queda negativo
 *   (saldo a favor).
 * - Total a pagar = Saldo anterior − Pagos + Total mes + Recargo mora.
 *
 * Con día de corte, el período es la ventana (periodStart, periodEnd]; sin
 * corte, el mes calendario. Un pago sin ninguna fecha cae en el mes de su cuota.
 *
 * @param {Object} params
 * @param {Array} params.apartments - [{id, number, owner, tenant, alicuota}]
 * @param {Array} params.payments - Todos los payments del edificio
 * @param {number} params.year
 * @param {number} params.month
 * @param {string|null} [params.periodStart] - Inicio de la ventana de corte (ISO)
 * @param {string|null} [params.periodEnd] - Fin de la ventana de corte (ISO)
 * @returns {Array} [{label, unitNumber, tipo, saldoAnterior, pagos, nuevoSaldo, gastosComunes, fondoReserva, totalMes, recargoMora, totalAPagar}]
 */
export function calculateUnitSummary({ apartments, payments, year, month, periodStart = null, periodEnd = null }) {
  const rows = [];
  const periodo = periodoDelResumen({ year, month, periodStart, periodEnd });

  apartments
    // Las unidades del edificio (portería, alquiler del edificio) no participan del
    // reparto de gastos comunes.
    .filter((a) => !esUnidadDelEdificio(a))
    .sort((a, b) => (a.number || "").localeCompare(b.number || "", undefined, { numeric: true }))
    .forEach((apt) => {
      const hasTenant = apt.tenant && String(apt.tenant).trim() !== "";
      // Cuotas de la unidad (mismo matching que Cobranza): las que dicen de qué
      // unidad son van solo a esa; las viejas sin unidad, por responsable.
      const unitPayments = payments.filter((p) => {
        const aptId = p.apartmentId || p.unitId;
        return aptId ? aptId === apt.id : true;
      });

      // Owner row (P) — incluir synthetic IDs (unit_XXX) para unidades sin propietario
      const ownerPayments = unitPayments.filter((p) =>
        String(p.userid) === String(apt.owner) || p.userid === `unit_${apt.id}`
      );
      rows.push(buildUnitRow(apt, "P", ownerPayments, periodo));

      // Tenant row (I) - only if tenant exists
      if (hasTenant) {
        const tenantPayments = unitPayments.filter((p) => String(p.userid) === String(apt.tenant));
        rows.push(buildUnitRow(apt, "I", tenantPayments, periodo));
      }
    });

  return rows;
}

// Dónde cae una cuota (por su etiqueta) y un cobro (por su fecha) respecto del
// período del resumen: "antes" | "mes" | "despues".
function periodoDelResumen({ year, month, periodStart, periodEnd }) {
  const useWindow = periodStart != null && periodEnd != null;
  const porEtiqueta = (y, m) => {
    if (y < year || (y === year && m < month)) return "antes";
    return y === year && m === month ? "mes" : "despues";
  };
  const porFecha = (d) => {
    if (useWindow) {
      if (isBeforeWindow(d, periodStart)) return "antes";
      return isInWindow(d, periodStart, periodEnd) ? "mes" : "despues";
    }
    return porEtiqueta(d.getFullYear(), d.getMonth() + 1);
  };
  return {
    cuota: (p) => porEtiqueta(Number(p.year), Number(p.month)),
    cobro: (p) => {
      const d = fechaDeCobro(p);
      return d ? porFecha(d) : porEtiqueta(Number(p.year), Number(p.month));
    },
  };
}

// Cargos de una cuota, por columna de la tabla.
function cargosDeCuota(p) {
  const b = p.breakdown;
  const customFunds = {};
  if (!b) {
    return { gastosComunes: Number(p.amount) || 0, fondoReserva: 0, otros: 0, customFunds, recargoMora: 0 };
  }
  let otros = Number(b.multas) || 0;
  Object.entries(b).forEach(([key, val]) => {
    // Multas, cargos puntuales y amenities itemizados
    if (key.startsWith("multa__") || key.startsWith("cargo__") || key.startsWith("amenity__")) otros += Number(val) || 0;
  });
  // Fondos personalizados (FIN-019): leer desde customFundAmounts o breakdown
  if (p.customFundAmounts) {
    Object.entries(p.customFundAmounts).forEach(([fid, amt]) => { customFunds[fid] = Number(amt) || 0; });
  } else {
    Object.entries(b).forEach(([key, val]) => {
      if (key.startsWith("fondo_") && key !== "fondo_reserva") customFunds[key.replace("fondo_", "")] = Number(val) || 0;
    });
  }
  return {
    gastosComunes: Number(b.gastos_comunes) || 0,
    fondoReserva: Number(b.fondo_reserva) || 0,
    otros,
    customFunds,
    // Intereses + reajuste por indexación UI
    recargoMora: (Number(b.intereses) || 0) + (Number(b.reajuste) || 0),
  };
}

const totalDeCargos = (c) =>
  c.gastosComunes + c.fondoReserva + c.otros + Object.values(c.customFunds).reduce((s, v) => s + v, 0);

function buildUnitRow(apt, tipo, userPayments, periodo) {
  const num = apt.number || apt.id;
  const label = `${num}${tipo}`;

  // Solo las cuotas (llevan mes y año); los registros sueltos no son cargos.
  const cuotas = userPayments.filter((p) => Number(p.month) > 0 && Number(p.year) > 0);

  let saldoAnterior = 0, pagosMes = 0;
  let gastosComunes = 0, fondoReserva = 0, otros = 0, recargoMora = 0;
  const customFunds = {};

  cuotas.forEach((p) => {
    const c = cargosDeCuota(p);
    const cuandoCuota = periodo.cuota(p);
    if (cuandoCuota === "antes") {
      saldoAnterior += totalDeCargos(c) + c.recargoMora;
    } else if (cuandoCuota === "mes") {
      gastosComunes += c.gastosComunes;
      fondoReserva += c.fondoReserva;
      otros += c.otros;
      recargoMora += c.recargoMora;
      Object.entries(c.customFunds).forEach(([fid, v]) => { customFunds[fid] = (customFunds[fid] || 0) + v; });
    }

    const cobrado = cobradoDelPago(p);
    if (!cobrado) return;
    const cuandoCobro = periodo.cobro(p);
    if (cuandoCobro === "antes") saldoAnterior -= cobrado;
    else if (cuandoCobro === "mes") pagosMes += cobrado;
  });

  const totalMes = totalDeCargos({ gastosComunes, fondoReserva, otros, customFunds });
  const nuevoSaldo = saldoAnterior - pagosMes;
  const totalAPagar = nuevoSaldo + totalMes + recargoMora;

  return {
    label,
    unitNumber: num,
    tipo,
    saldoAnterior: roundMoney(saldoAnterior),
    pagos: roundMoney(pagosMes),
    nuevoSaldo: roundMoney(nuevoSaldo),
    gastosComunes: roundMoney(gastosComunes),
    fondoReserva: roundMoney(fondoReserva),
    customFunds, // Montos por fondo personalizado { [fundId]: amount }
    totalMes: roundMoney(totalMes),
    recargoMora: roundMoney(recargoMora),
    totalAPagar: roundMoney(totalAPagar),
  };
}

/**
 * Calcula totales de la tabla resumen de unidades.
 */
export function calculateUnitSummaryTotals(rows) {
  const totals = rows.reduce(
    (totals, r) => {
      const acc = {
        saldoAnterior: totals.saldoAnterior + r.saldoAnterior,
        pagos: totals.pagos + r.pagos,
        nuevoSaldo: totals.nuevoSaldo + r.nuevoSaldo,
        gastosComunes: totals.gastosComunes + r.gastosComunes,
        fondoReserva: totals.fondoReserva + r.fondoReserva,
        totalMes: totals.totalMes + r.totalMes,
        recargoMora: totals.recargoMora + r.recargoMora,
        totalAPagar: totals.totalAPagar + r.totalAPagar,
        customFunds: { ...totals.customFunds },
      };
      // Agregar totales por fondo personalizado (FIN-019)
      if (r.customFunds) {
        for (const [fid, amt] of Object.entries(r.customFunds)) {
          acc.customFunds[fid] = (acc.customFunds[fid] || 0) + (Number(amt) || 0);
        }
      }
      return acc;
    },
    { saldoAnterior: 0, pagos: 0, nuevoSaldo: 0, gastosComunes: 0, fondoReserva: 0, totalMes: 0, recargoMora: 0, totalAPagar: 0, customFunds: {} }
  );
  return totals;
}

// --- Helpers ---

/**
 * Distribuye el breakdown de un payment entre caja, reserva y fondos custom.
 * Maneja keys built-in (gastos_comunes, fondo_reserva, multas, intereses, obras),
 * fondos custom (fondo_*), y keys itemizadas (multa__*, amenity__*).
 *
 * Las multas y amenities itemizadas se rutean según amenityByFund/multaByFund del payment,
 * o por defecto a reserva (mismo comportamiento que el legacy "multas").
 */
export function distributeBreakdown(breakdown, payment, paidAmount) {
  const b = breakdown;
  let caja = 0, reserva = 0;
  const custom = {};
  let total = 0;

  // Built-in keys
  const gc = Number(b.gastos_comunes) || 0;
  const fr = Number(b.fondo_reserva) || 0;
  const mu = Number(b.multas) || 0; // legacy flat field
  const int = Number(b.intereses) || 0;
  const ob = Number(b.obras) || 0;
  const rj = Number(b.reajuste) || 0; // reajuste por indexación UI (mora Ley 19.604) → reserva
  // Alquiler de una unidad del edificio: cuenta como línea reconocida (si no, el pago
  // caería entero a caja por el fallback de "sin desglose"), pero no es cobranza de
  // ningún fondo: entra como ingreso (ingresosDeAlquiler). Se devuelve aparte.
  const alq = Number(b.alquiler) || 0;

  caja += gc;
  reserva += fr + mu + int + ob + rj;
  total += gc + fr + mu + int + ob + rj + alq;

  // Multas itemizadas (multa__*) — rutear según multaByFund o default reserva
  Object.entries(b).forEach(([key, val]) => {
    if (!key.startsWith("multa__")) return;
    const amt = Number(val) || 0;
    if (amt <= 0) return;
    total += amt;
    // Determinar fondo destino desde multaByFund del payment
    const multaByFund = payment?.multaByFund || {};
    const totalMultas = Object.values(multaByFund).reduce((s, v) => s + v, 0);
    if (totalMultas > 0 && multaByFund.caja) {
      // Proporción que va a caja
      const cajaRatio = multaByFund.caja / totalMultas;
      caja += amt * cajaRatio;
      reserva += amt * (1 - cajaRatio);
    } else {
      // Default: multas van a reserva
      reserva += amt;
    }
  });

  // Amenities itemizados (amenity__*) — rutear según amenityByFund o default reserva
  Object.entries(b).forEach(([key, val]) => {
    if (!key.startsWith("amenity__")) return;
    const amt = Number(val) || 0;
    if (amt <= 0) return;
    total += amt;
    const amenityByFund = payment?.amenityByFund || {};
    const totalAmenity = Object.values(amenityByFund).reduce((s, v) => s + v, 0);
    if (totalAmenity > 0) {
      // Distribuir proporcionalmente según los fondos configurados
      Object.entries(amenityByFund).forEach(([fid, fundAmt]) => {
        const ratio = fundAmt / totalAmenity;
        const portion = amt * ratio;
        if (fid === "caja") caja += portion;
        else if (fid === "reserva") reserva += portion;
        else custom[fid] = (custom[fid] || 0) + portion;
      });
    } else {
      reserva += amt;
    }
  });

  // Cargos puntuales itemizados (cargo__*) — rutear según chargeByFund o default caja
  Object.entries(b).forEach(([key, val]) => {
    if (!key.startsWith("cargo__")) return;
    const amt = Number(val) || 0;
    if (amt <= 0) return;
    total += amt;
    const chargeByFund = payment?.chargeByFund || {};
    const totalCharge = Object.values(chargeByFund).reduce((s, v) => s + v, 0);
    if (totalCharge > 0) {
      Object.entries(chargeByFund).forEach(([fid, fundAmt]) => {
        const ratio = fundAmt / totalCharge;
        const portion = amt * ratio;
        if (fid === "caja") caja += portion;
        else if (fid === "reserva") reserva += portion;
        else custom[fid] = (custom[fid] || 0) + portion;
      });
    } else {
      // Default: cargos puntuales van a caja (gasto operativo de la unidad)
      caja += amt;
    }
  });

  // Fondos custom (fondo_*) — excepto fondo_reserva
  Object.entries(b).forEach(([key, val]) => {
    if (!key.startsWith("fondo_") || key === "fondo_reserva") return;
    const amt = Number(val) || 0;
    if (amt <= 0) return;
    total += amt;
    const fid = key.replace("fondo_", "");
    custom[fid] = (custom[fid] || 0) + amt;
  });

  // Aplicar ratio de pago parcial. El excedente de un sobrepago (paidAmount > amount)
  // NO entra a los fondos: queda como saldo a favor de la unidad (pool aparte). Ratio ≤ 1.
  const ratio = Math.min(1, paidAmount / (Number(payment?.amount) || paidAmount || 1));

  return {
    caja: caja * ratio,
    reserva: reserva * ratio,
    custom: Object.fromEntries(
      Object.entries(custom).map(([k, v]) => [k, v * ratio])
    ),
    alquiler: alq * ratio,
    hasData: total > 0,
  };
}

function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    // "YYYY-MM-DD" se parsea como medianoche UTC por new Date(), lo que desplaza la
    // fecha un día atrás en UY (UTC-3) y podía mal-bucketear gastos de borde de mes.
    // Se ancla a mediodía local (mismo criterio que dates.js / fundMovements).
    const s = val.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(val) ? val + "T12:00:00" : val;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}
